import React, { Component } from "react";
import { PopUp } from "./PopUp";
import { IconContext } from "react-icons";
import { FaCheck, ImLeaf } from "react-icons/all";
import { FirebaseService } from "../../services/firebase/FirebaseService";
import { RequestPayload } from "../sites/types/RequestPayload";
import { ReadAtDatabaseService } from "../../services/database/ReadAtDatabaseService";
import { UpdateAtDatabaseService } from "../../services/database/UpdateAtDatabaseService";
import { DeleteAtDatabaseService } from "../../services/database/DeleteAtDatabaseService";
import { Link } from "react-router-dom";

/**
 * React Component properties
 */
type Props = {
  // data
  isVisible: boolean;
  searchResult: RequestPayload;
  trackingID: string;

  // function
  onHide: () => void;
};

/**
 * The possible actions when the receiver is not there
 */
export enum NotPresentAction {
  SIGNATURE_RELEASE_AUTHORISATION = "signatureReleaseAuthorisation",
  NEIGHBOR_RELEASE = "neighborRelease",
  DECLINE = "decline",
}

/**
 * The click steps of the popUp
 */
enum SIGNATURE_RELEASE_STEP {
  ACCEPT_CONTRACT = "acceptContract",
  ENTER_RECEIVER_INFO = "enterReceiverInfo",
  ENTER_CODE = "enterCode",
}

/**
 * Local interface to define the types of the state
 */
interface IState {
  currentStep: SIGNATURE_RELEASE_STEP | undefined;
  notPresentAction: NotPresentAction | undefined;
  acceptedContract: boolean;
  releasePlace?: string;
  releaseExtraInfos?: string;
  code?: string;
}

/**
 * Cross Innovation Class 2021
 *
 * Team: Kühne + Nagel
 *
 * Kunden Web App
 *
 * This component is a popUp that opens when the user is not there
 *
 * @author Tobias Ramm <winf103614@fh-wedel,de>
 * @version last modification: 29.06.2021
 *
 */
export class NotTherePopUp extends Component<Props> {
  state: Readonly<IState> = {
    notPresentAction: this.props.searchResult.chosenNotPresentOption,
    currentStep: undefined,
    acceptedContract: false,
  };

  render() {
    const { currentStep } = this.state;

    return (
      <PopUp isVisible={this.props.isVisible} onHide={this.props.onHide}>
        {!currentStep && this.renderPopUp()}
        {currentStep === SIGNATURE_RELEASE_STEP.ACCEPT_CONTRACT &&
          this.renderAcceptContract()}
        {currentStep === SIGNATURE_RELEASE_STEP.ENTER_RECEIVER_INFO &&
          this.renderEnterReceiverCode()}
        {currentStep === SIGNATURE_RELEASE_STEP.ENTER_CODE &&
          this.renderEnterCode()}
      </PopUp>
    );
  }

  /**
   * Renders the initial popUp view
   */
  renderPopUp(): JSX.Element {
    const { notPresentAction } = this.state;

    return (
      <div className={"not_there_pop_up_container"}>
        <h2>Sie sind nicht da:</h2>

        <p>Bitte wählen Sie eine der unten aufgelisteten Hilfsangebote aus.</p>

        <div className={"not_there_pop_up_choice_container"}>
          {/* Option 1. SIGNATURE RELEASE AUTHORISATION */}
          <div
            className={
              "not_there_pop_up_choice_singleChoice " +
              (notPresentAction ===
              NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION
                ? " active_choice"
                : " inactive_choice")
            }
            onClick={() => {
              if (
                notPresentAction ===
                NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION
              ) {
                this.setState({ notPresentAction: undefined });
              } else {
                this.setState({
                  notPresentAction:
                    NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION,
                });
              }
            }}
          >
            <div
              className={
                "checked_icon_container" +
                (notPresentAction ===
                NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION
                  ? " active_choice"
                  : " inactive_choice")
              }
            >
              {notPresentAction ===
                NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION && (
                <IconContext.Provider
                  value={{ color: "#6BC963", className: "icon" }}
                >
                  <FaCheck />
                </IconContext.Provider>
              )}
            </div>

            <h3>Abstellgenehmigung</h3>

            <p>
              dadurch können wir Ihre Lieferung auch ohne Ihre Anwesenheit bei
              Ihnen zustellen.
            </p>

            <p>Dies geht bequem und schnell online.</p>
          </div>

          {/* Option 2. NEIGHBOR RELEASE */}
          <div
            className={
              "not_there_pop_up_choice_singleChoice " +
              (notPresentAction === NotPresentAction.NEIGHBOR_RELEASE
                ? " active_choice"
                : " inactive_choice")
            }
            onClick={() => {
              if (notPresentAction === NotPresentAction.NEIGHBOR_RELEASE) {
                this.setState({ notPresentAction: undefined });
              } else {
                this.setState({
                  notPresentAction: NotPresentAction.NEIGHBOR_RELEASE,
                });
              }
            }}
          >
            <div
              className={
                "checked_icon_container" +
                (notPresentAction === NotPresentAction.NEIGHBOR_RELEASE
                  ? " active_choice"
                  : " inactive_choice")
              }
            >
              {notPresentAction === NotPresentAction.NEIGHBOR_RELEASE && (
                <IconContext.Provider
                  value={{ color: "#6BC963", className: "icon" }}
                >
                  <FaCheck />
                </IconContext.Provider>
              )}
            </div>

            <h3>Nachbar-Zustellung</h3>

            <p>
              wählen Sie einen Nachbar aus, der für Sie die Lieferung annimmt.
            </p>

            <p>Sie müssen dafür ein Formular ausfüllen.</p>
          </div>

          {/* Option 3. DECLINE */}
          <div
            className={
              "not_there_pop_up_choice_singleChoice " +
              (notPresentAction === NotPresentAction.DECLINE
                ? " active_choice"
                : " inactive_choice")
            }
            onClick={() => {
              if (notPresentAction === NotPresentAction.DECLINE) {
                this.setState({ notPresentAction: undefined });
              } else {
                this.setState({
                  notPresentAction: NotPresentAction.DECLINE,
                });
              }
            }}
          >
            <div
              className={
                "checked_icon_container" +
                (notPresentAction === NotPresentAction.DECLINE
                  ? " active_choice"
                  : " inactive_choice")
              }
            >
              {notPresentAction === NotPresentAction.DECLINE && (
                <IconContext.Provider
                  value={{ color: "#6BC963", className: "icon" }}
                >
                  <FaCheck />
                </IconContext.Provider>
              )}
            </div>

            <h3>Lieferung Stornieren</h3>

            <p>die Zustellung Ihrer Lieferung wird gestoppt.</p>

            <div className={"environment_info"}>
              <IconContext.Provider
                value={{ color: "#6BC963", className: "icon" }}
              >
                <ImLeaf />
              </IconContext.Provider>

              <p>Die Lieferung zu stornieren belastet unnötig die Umwelt! </p>
            </div>

            <Link to="/unsuccessfulDelivery" target={"_blank"}>
              <h4>Lernen Sie hier was dies für die Umwelt bedeutet</h4>
            </Link>
          </div>
        </div>

        {notPresentAction ===
          NotPresentAction.SIGNATURE_RELEASE_AUTHORISATION && (
          <button
            onClick={async () => {
              this.setState({
                currentStep: SIGNATURE_RELEASE_STEP.ACCEPT_CONTRACT,
              });
            }}
          >
            Fortfahren
          </button>
        )}

        {(notPresentAction === NotPresentAction.NEIGHBOR_RELEASE ||
          notPresentAction === NotPresentAction.DECLINE ||
          !notPresentAction) && (
          <button
            onClick={async () => {
              switch (notPresentAction) {
                case NotPresentAction.DECLINE:
                  await this.handleDeclineDelivery(this.props.trackingID);
                  break;
                case NotPresentAction.NEIGHBOR_RELEASE:
                  await this.handleNeighbourDelivery(this.props.trackingID);
                  break;
                default:
                  await this.removeIsPresent(this.props.trackingID);
              }

              this.props.onHide();
            }}
          >
            Speichern
          </button>
        )}
      </div>
    );
  }

  /**
   * Renders the contract of the signature release
   */
  renderAcceptContract() {
    return (
      <div>
        <h3>Abstellgenehmigung erteilen:</h3>

        <p>
          Der / die Anlieferfahrer(in) wird durch uns bevollmächtigt die
          Empfangsbescheinigung im Sinne von Ziffer 8.2 ADSp mit befreiender
          Wirkung im Sinne von Ziffer 13. ADSp (jeweils Stand 01.01.2003) in
          unserem Auftrag zu unterzeichnen. Bei Anlieferung bereits bestehende,
          äußerlich erkennbare Beschädigungen oder Verluste des Transportgutes
          sind von dem / der Anlieferfahrer(in) wahrheitsgemäß zu vermerken. Für
          Beschädigungen, Verluste oder sonstige Schäden, welche durch das
          Abstellen des Transportgutes entstanden sind oder sein können ist der
          Spediteur von jedweder Haftung befreit. Dies gilt nicht im Falle einer
          Verletzung von Leben, Körper oder Gesundheit oder bei grober
          Fahrlässigkeit. § 427 Abs. 2 HGB ist auf diesen
          Haftungsausschlussgrund analog anzuwenden. Dies ist als Weisung im
          Sinne des § 418 Abs. 2 HGB anzusehen.
        </p>

        <label>
          <input
            type="checkbox"
            checked={this.state.acceptedContract}
            onChange={(evt) =>
              this.setState({ acceptedContract: evt.target.checked })
            }
          />
          Ich habe den Vertrag gelesen und stimme diesem zu.
        </label>

        <button
          onClick={async () => {
            this.setState({
              currentStep: SIGNATURE_RELEASE_STEP.ENTER_RECEIVER_INFO,
            });
          }}
          disabled={!this.state.acceptedContract}
          style={
            this.state.acceptedContract ? { opacity: 1 } : { opacity: 0.4 }
          }
        >
          Fortfahren
        </button>
      </div>
    );
  }

  /**
   * Renders the input of the needed data of the signature release
   */
  renderEnterReceiverCode() {
    return (
      <div>
        <h3>Abstellgenehmigung erteilen:</h3>

        <label>
          Abstellort
          <input
            type={"text"}
            placeholder={"z.B. Schuppen"}
            value={this.state.releasePlace}
            className={"input"}
            onChange={(evt) =>
              this.setState({ releasePlace: evt.target.value })
            }
          />
        </label>

        <label>
          Weitere Informationen (Optional)
          <input
            type={"text"}
            placeholder={"Gibt es irgendwelche Besonderheiten?"}
            value={this.state.releaseExtraInfos}
            className={"input"}
            onChange={(evt) =>
              this.setState({ releaseExtraInfos: evt.target.value })
            }
          />
        </label>

        <button
          onClick={async () => {
            const handleVerifyCode = FirebaseService.functions().httpsCallable(
              "handleVerifyCode"
            );

            handleVerifyCode({
              receiverEmail: this.props.searchResult.receiverEmail,
            }).catch((e) =>
              console.error("Error: Returned from cloud function", e)
            );

            this.setState({
              currentStep: SIGNATURE_RELEASE_STEP.ENTER_CODE,
            });
          }}
          disabled={!this.state.releasePlace}
          style={this.state.releasePlace ? { opacity: 1 } : { opacity: 0.4 }}
        >
          Fortfahren
        </button>
      </div>
    );
  }

  /**
   * Renders the input of the authentication that was send via email
   */
  renderEnterCode() {
    return (
      <div>
        <h3>Abstellgenehmigung erteilen:</h3>

        <p>
          wir haben Ihnen zur Verifizierung einen 6 stelligen Code an die
          E-Mail: {this.props.searchResult.receiverEmail} gesendet.
        </p>
        <p>
          Geben Sie diesen bitte hier ein, um die Abstellgenehmigung zu
          erteilen:
        </p>
        <label>
          Verifizierungscode
          <input
            type={"text"}
            placeholder={"6-stellig"}
            value={this.state.code}
            className={"input"}
            onChange={(evt) => this.setState({ code: evt.target.value })}
          />
        </label>

        <button
          onClick={async () => this.handleSetSignatureReleaseAuthorisation()}
          disabled={!this.state.code}
          style={this.state.code ? { opacity: 1 } : { opacity: 0.4 }}
        >
          Abstellgenehmigung erteilen
        </button>
      </div>
    );
  }

  /**
   * Verifies that the authentication code is valid
   */
  async handleSetSignatureReleaseAuthorisation() {
    const { code } = this.state;

    const snap = await ReadAtDatabaseService.getAuthenticationCode(
      code as string
    );

    if (snap.val() != null) {
      await UpdateAtDatabaseService.updateNotThereOption(this.props.trackingID);
      this.props.onHide();
    }
  }

  /**
   * Handles the cancel of a delivery
   *
   * @param trackingID
   */
  async handleDeclineDelivery(trackingID: string) {
    await UpdateAtDatabaseService.updateDeclineDelivery(trackingID);
    await this.removeIsPresent(trackingID);
  }

  /**
   * Handles the neighbour release
   *
   * @param trackingID
   */
  async handleNeighbourDelivery(trackingID: string) {
    await UpdateAtDatabaseService.updateNeighbourRelease(trackingID);
    await this.removeIsPresent(trackingID);
  }

  /**
   * Removes that the receiver is present to accept the delivery
   *
   * @param trackingID
   */
  async removeIsPresent(trackingID: string) {
    await DeleteAtDatabaseService.removeReceiverIsPresent(trackingID);
  }
}
